import React, { PureComponent } from 'react';
import { defineMessages } from 'react-intl.macro';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Field } from 'redux-form/immutable';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/InfoOutlined';

import { SvgIconWrapper, Tooltip } from 'embeds/components';

import FormEnhancedTextField from 'embeds/components/ReduxForm/FormEnhancedTextField';
import SavedTemplateButton from 'embeds/components/SavedTemplateButton';
import SquigglyTemplatesButton from 'embeds/components/SquigglyTemplatesButton';
import NewFormCheckbox from 'embeds/components/Form/NewFormCheckbox';

const BROADCAST_CONTEXT = 'BROADCAST_CONTEXT';
const CHAT_HUB_CONTEXT = 'CHAT_HUB_CONTEXT';

const styles = (theme) => {
  const {
    palette: {
      common: { white },
    },
    typography: { fontFamily },
    spacing: { unit },
  } = theme;

  return {
    hidden: {
      display: 'none !important',
    },
    messageButtons: {
      fontFamily,
      display: 'flex',
      alignItems: 'center',
      float: 'left',
      padding: unit * 2,
      width: '100%',
    },
    actionButton: {
      fontFamily,
      marginRight: unit * 2,
    },
    messageField: {
      padding: `${unit * 2}px ${unit * 2}px ${unit}px ${unit * 2}px !important`,
    },
    codeEditorWrapper: {
      display: 'inline-flex',
      width: '100%',
      padding: `${unit * 3}px ${unit * 2}px ${unit}px ${unit * 2}px !important`,
    },
    messageFieldContainer: {
      display: 'inline-block',
      float: 'left',
      width: '100%',
    },
    messageWrapper: {
      backgroundColor: white,
      border: `1px solid ${white}`,
      display: 'inline-block',
      float: 'left',
      width: '100%',
    },
    inputWrapper: {
      display: 'flex',
    },
    root: {
      display: 'inline-block',
      width: '100%',
    },
  };
};

const messages = defineMessages({
  secureChatInfoTooltip: {
    id: 'embeds.components.TemplateTextArea.secureChatInfoTooltip',
    defaultMessage:
      'When checked, this broadcast will be sent as a secure chat for SMS and email. Patients with an active voice contact will receive a call instead.',
  },
});

export class TemplateTextArea extends PureComponent {
  constructor(props) {
    super(props);

    this.textareaInfo = {};
    this.templateAdded = '';
  }

  handleClearRequest = () => {
    const { onClearRequest } = this.props;

    if (onClearRequest) onClearRequest();
  };

  handleGetCursorPointer = (textareaInfo) => {
    this.textareaInfo = textareaInfo || {};
  };

  handleSavedTemplateButtonClick = (event) => {
    const { onSavedTemplateButtonClick } = this.props;

    if (onSavedTemplateButtonClick) onSavedTemplateButtonClick(event);
  };

  handleSquigglyTemplateSelected = (selectedTemplate) => {
    const { onSquigglyTemplateSelected, value } = this.props;
    const { cursorEnd, cursorStart } = this.textareaInfo;
    const newValue = `${value.slice(0, cursorStart || value.length)} ${selectedTemplate.id} ${value.slice(
      cursorEnd || value.length,
    )}`;

    this.templateAdded = selectedTemplate.id;

    onSquigglyTemplateSelected(newValue);
  };

  render() {
    const {
      classes,
      className,
      disabled,
      hintText,
      name,
      onChange,
      selectedTemplateMessage,
      showSavedTemplateButton,
      squigglyTemplates,
      context,
      onAutoCloseChatSelected,
      onSendAsSecureChat,
      useCodeEditor,
    } = this.props;

    return (
      <div className={classnames(classes.root, className)}>
        <div className={classes.messageWrapper}>
          <div className={useCodeEditor ? classes.codeEditorWrapper : classes.messageFieldContainer}>
            <Field
              className={classes.messageField}
              component={FormEnhancedTextField}
              disabled={Boolean(disabled)}
              externalTemplateAdded={this.templateAdded}
              ignoreInvalidSquigglies={Boolean(selectedTemplateMessage)}
              hintText={hintText}
              name={name}
              onChange={onChange}
              onInfoUpdated={this.handleGetCursorPointer}
              squigglyTemplates={squigglyTemplates}
              useCodeEditor={useCodeEditor}
            />
          </div>
          <div
            className={classnames(classes.messageButtons, { [classes.hidden]: !showSavedTemplateButton && disabled })}
          >
            <SquigglyTemplatesButton
              className={classes.actionButton}
              hide={Boolean(squigglyTemplates.length < 1 || disabled || selectedTemplateMessage)}
              label="Insert Squiggly"
              onSquigglyTemplateSelected={this.handleSquigglyTemplateSelected}
              squigglyTemplates={squigglyTemplates}
            />
            {context === CHAT_HUB_CONTEXT && (
              <Field
                name="autoCloseChat"
                label="Automatically close patient chat"
                component={NewFormCheckbox}
                labelPosition="right"
                onChange={onAutoCloseChatSelected}
              />
            )}
            <SavedTemplateButton
              className={classes.actionButton}
              disabled={Boolean(disabled && selectedTemplateMessage)}
              hide={!showSavedTemplateButton || (disabled && !selectedTemplateMessage)}
              onClearRequest={this.handleClearRequest}
              onClick={this.handleSavedTemplateButtonClick}
              selectedTemplateMessage={selectedTemplateMessage}
            />
            {context === BROADCAST_CONTEXT && (
              <>
                <div className={classes.inputWrapper}>
                  <Field
                    disabled={Boolean(disabled)}
                    name="secureChat"
                    label="Send as secure chat"
                    component={NewFormCheckbox}
                    labelPosition="right"
                    onChange={onSendAsSecureChat}
                  />
                </div>
                <Tooltip title={<FormattedMessage {...messages.secureChatInfoTooltip} />} hideArrow placement="top">
                  <SvgIconWrapper Icon={InfoIcon} fontSize="small" color="supportSteel" />
                </Tooltip>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}

TemplateTextArea.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  hintText: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onClearRequest: PropTypes.func,
  onSquigglyTemplateSelected: PropTypes.func.isRequired,
  onSavedTemplateButtonClick: PropTypes.func,
  onSendAsSecureChat: PropTypes.func,
  selectedTemplateMessage: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  showSavedTemplateButton: PropTypes.bool,
  squigglyTemplates: PropTypes.array,
  value: PropTypes.string,
  useCodeEditor: PropTypes.bool,
};

TemplateTextArea.defaultProps = {
  disabled: false,
  hintText: '',
  showSavedTemplateButton: false,
  value: '',
  useCodeEditor: false,
};

export default withStyles(styles)(TemplateTextArea);
